import { ComponentType, lazy } from "react";

type ComponentPromise<T> = Promise<{ default: ComponentType<T> }>;

// a function to retry loading a chunk to avoid chunk load error for out of date code
export const lazyRetry = <T>(
  component: () => ComponentPromise<T>,
  retries?: number,
  interval?: number
): React.LazyExoticComponent<React.ComponentType<T>> => {
  return lazy(() => retry(component, retries, interval));
};

const retry = <T>(fn: () => ComponentPromise<T>, retriesLeft = 3, interval = 1000): ComponentPromise<T> => {
  return new Promise((resolve, reject) => {
    // check if the window has already been refreshed
    const hasRefreshed = JSON.parse(sessionStorage.getItem("retry-lazy-refreshed") || "false");
    // try to import the component
    fn()
      .then((component) => {
        sessionStorage.setItem("retry-lazy-refreshed", "false"); // success so reset the refresh
        resolve(component);
      })
      .catch((error) => {
        if (!hasRefreshed) {
          // not been refreshed yet
          sessionStorage.setItem("retry-lazy-refreshed", "true"); // we are now going to refresh
          return location.reload(); // refresh the page
        }
        setTimeout(() => {
          if (retriesLeft === 1) {
            reject(error);
            return;
          }
          retry(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
};
